/**
 * Tree breadcrumbs: displays the chain of ancestor pages (root → current)
 * and allows navigation to any level. No dead ends: there is always
 * a route back upward. Derived from the in-memory page list.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { ChevronRight, Home } from "@fleet/icons";
import type { Page } from "@folio/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { usePageTree } from "../hooks/use-page-tree";

interface PageBreadcrumbsProps {
  pageId: string;
}

const MAX_DEPTH = 12; // defensively cuts off pathological chains / cycles

export function PageBreadcrumbs(props: PageBreadcrumbsProps) {
  const { t } = useI18n();
  const { pages } = usePageTree();

  const byId = createMemo(() => {
    const map = new Map<string, Page>();
    for (const p of pages.data ?? []) {
      map.set(p.id, p);
    }
    return map;
  });

  // Ancestor chain, from the root to the current page (excluded).
  const ancestors = createMemo<Page[]>(() => {
    const map = byId();
    const chain: Page[] = [];
    const seen = new Set<string>();
    let current = map.get(props.pageId)?.parent_page_id ?? null;
    let depth = 0;
    while (current && !seen.has(current) && depth < MAX_DEPTH) {
      const parent = map.get(current);
      if (!parent) {
        break;
      }
      seen.add(current);
      chain.unshift(parent);
      current = parent.parent_page_id ?? null;
      depth += 1;
    }
    return chain;
  });

  const label = (page: Page) => page.title?.trim() || t(msg`Untitled`);

  return (
    <Show when={ancestors().length > 0}>
      <nav aria-label={t(msg`Breadcrumb`)} class="mb-2 min-w-0">
        <ol class="flex flex-wrap items-center gap-1 text-xs text-muted-foreground/70">
          <li>
            <Link
              to="/documents"
              class="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-accent/10 hover:text-foreground"
              aria-label={t(msg`All pages`)}
            >
              <Home class="h-3 w-3" />
            </Link>
          </li>
          <For each={ancestors()}>
            {(page) => (
              <>
                <li aria-hidden="true">
                  <ChevronRight class="h-3 w-3 opacity-50" />
                </li>
                <li class="min-w-0">
                  <Link
                    to="/documents/$pageId"
                    params={{ pageId: page.id }}
                    class="flex max-w-[10rem] items-center gap-1 truncate rounded px-1 py-0.5 hover:bg-accent/10 hover:text-foreground"
                  >
                    <span class="shrink-0 leading-none">{page.icon ?? "📄"}</span>
                    <span class="truncate">{label(page)}</span>
                  </Link>
                </li>
              </>
            )}
          </For>
        </ol>
      </nav>
    </Show>
  );
}
